import { DocsLayout } from "@/components/docs-layout";
import { DocsNavigation } from "@/components/docs-navigation";
import { Gift, TrendingUp, Users, DollarSign, Percent, ChevronRight, HelpCircle } from "lucide-react";

export const AffiliateInfo = () => {
  return (
    <DocsLayout>
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center gap-3 mb-2">
          <Gift className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Affiliate Program</h1>
        </div>
        
        <div className="text-sm text-muted-foreground mb-8">
          <p>Earn rewards by referring traders to Nordex</p>
        </div>

        {/* Overview */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Program Overview</h2>
          <p className="text-muted-foreground mb-4">
            The Nordex Affiliate Program allows you to earn commissions by referring traders to our platform. 
            As an affiliate, you'll receive a share of the trading fees generated by your referrals, 
            plus optional revenue share bonuses.
          </p>

          <div className="grid gap-4 md:grid-cols-2 mt-6">
            <div className="p-5 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                </div>
                <h3 className="font-semibold">Fee Share Commission</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Earn a percentage of trading fees generated by your referred traders. 
                Commission rates are customized based on your audience size and reach.
              </p>
            </div>

            <div className="p-5 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-violet-500/10">
                  <Percent className="h-5 w-5 text-violet-500" />
                </div>
                <h3 className="font-semibold">Revenue Share</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Eligible affiliates can earn additional revenue share bonuses 
                based on overall platform revenue from referrals.
              </p>
            </div>

            <div className="p-5 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold">Referral Discounts</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Your referrals receive trading fee discounts, 
                making your link more attractive to new traders.
              </p>
            </div>

            <div className="p-5 border border-border rounded-lg bg-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <DollarSign className="h-5 w-5 text-amber-500" />
                </div>
                <h3 className="font-semibold">Signup Bonuses</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Optionally offer signup bonuses to new referrals, paid from your earnings, 
                to incentivize signups.
              </p>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">How It Works</h2>
          
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                1
              </div>
              <div>
                <h3 className="font-semibold mb-1">Apply to the Program</h3>
                <p className="text-sm text-muted-foreground">
                  Navigate to the Affiliate page and submit your application. Include information about 
                  your audience, marketing channels, and how you plan to promote Nordex.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                2
              </div>
              <div>
                <h3 className="font-semibold mb-1">Get Approved</h3>
                <p className="text-sm text-muted-foreground">
                  Our team reviews applications within 24-48 hours. Once approved, you'll receive 
                  your custom referral link and access to the affiliate dashboard.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                3
              </div>
              <div>
                <h3 className="font-semibold mb-1">Share Your Link</h3>
                <p className="text-sm text-muted-foreground">
                  Share your referral link through your marketing channels: social media, YouTube, 
                  Twitter, Discord, blog posts, or anywhere your audience is active.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                4
              </div>
              <div>
                <h3 className="font-semibold mb-1">Earn Commissions</h3>
                <p className="text-sm text-muted-foreground">
                  When traders sign up using your link and start trading, you automatically earn 
                  commissions on their trading fees. Track everything in your affiliate dashboard.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                5
              </div>
              <div>
                <h3 className="font-semibold mb-1">Request Payouts</h3>
                <p className="text-sm text-muted-foreground">
                  Once you reach the minimum payout threshold, request a withdrawal directly from 
                  your affiliate dashboard. Payouts are processed in USDC to your wallet.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Commission Structure */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Commission Structure</h2>
          
          <div className="p-6 border border-border rounded-lg bg-card mb-6">
            <h3 className="font-semibold mb-4">Commission Structure Example</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Your referral generates trading fees:</span>
                <span className="font-semibold">$1,000</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Your commission rate:</span>
                <span className="font-semibold">Custom %</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-muted/30">
                <span className="text-muted-foreground">Referral discount:</span>
                <span className="font-semibold text-amber-500">Custom %</span>
              </div>
              <div className="flex justify-between items-center p-3 rounded-lg bg-primary/10 border border-primary/20">
                <span className="font-semibold">Your potential earnings:</span>
                <span className="font-bold text-primary text-lg">Negotiable</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              * Commission rates are customized based on your audience and performance. Plus any applicable revenue share bonuses.
            </p>
          </div>

          <div className="p-5 bg-primary/5 border border-primary/20 rounded-lg">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Lifetime Commissions
            </h3>
            <p className="text-sm text-muted-foreground">
              You continue to earn commissions for the lifetime of your referred traders. 
              As long as they're trading on Nordex, you're earning.
            </p>
          </div>
        </section>

        {/* Tracking & Tools */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Tracking & Tools</h2>
          
          <p className="text-muted-foreground mb-4">
            Your affiliate dashboard provides comprehensive tools to track and optimize your performance:
          </p>

          <ul className="space-y-3">
            <li className="flex items-start gap-3">
              <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Real-time Statistics:</span>
                <span className="text-sm text-muted-foreground"> Track referrals, active traders, total earnings, and pending payouts</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Earnings Chart:</span>
                <span className="text-sm text-muted-foreground"> Visualize your earnings over time with detailed breakdowns</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Referral Table:</span>
                <span className="text-sm text-muted-foreground"> See individual referral performance, trading volume, and commissions generated</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Custom Links:</span>
                <span className="text-sm text-muted-foreground"> Generate vanity URLs (nordex.trade/join/yourname) for better branding</span>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <ChevronRight className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Payout History:</span>
                <span className="text-sm text-muted-foreground"> Complete history of all your payouts and pending withdrawals</span>
              </div>
            </li>
          </ul>
        </section>

        {/* Requirements & Guidelines */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold mb-6">Requirements & Guidelines</h2>
          
          <div className="space-y-4">
            <div className="p-4 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Minimum Requirements</h3>
              <ul className="text-sm text-muted-foreground space-y-2 ml-5 list-disc">
                <li>Active social media presence or content platform</li>
                <li>Clear marketing plan or promotion strategy</li>
                <li>Compliance with all applicable laws and regulations</li>
                <li>Minimum payout threshold will be discussed during onboarding</li>
              </ul>
            </div>

            <div className="p-4 border border-border rounded-lg bg-card">
              <h3 className="font-semibold mb-2">Prohibited Activities</h3>
              <ul className="text-sm text-muted-foreground space-y-2 ml-5 list-disc">
                <li>False or misleading advertising</li>
                <li>Spamming or unsolicited messages</li>
                <li>Impersonating Nordex or its employees</li>
                <li>Violating platform terms of service</li>
                <li>Creating fake accounts to generate referrals</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Get Started */}
        <section>
          <div className="p-8 border border-border rounded-xl bg-gradient-to-br from-primary/5 to-primary/10">
            <h2 className="text-2xl font-semibold mb-3">Ready to Get Started?</h2>
            <p className="text-muted-foreground mb-6">
              Join the Nordex Affiliate Program and start earning commissions today. 
              Navigate to the Affiliate page to apply or manage your account.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/affiliate"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-colors"
              >
                <Gift className="h-5 w-5" />
                Go to Affiliate Dashboard
              </a>
              <a
                href="/support"
                className="inline-flex items-center gap-2 px-6 py-3 border border-border rounded-lg font-semibold hover:bg-accent transition-colors"
              >
                <HelpCircle className="h-5 w-5" />
                Contact Support
              </a>
            </div>
          </div>
        </section>

        {/* Navigation */}
        <DocsNavigation
          previous={{ title: "Referral Program", href: "/referrals-info" }}
          next={{ title: "Terms of Use", href: "/terms" }}
        />
      </div>
    </DocsLayout>
  );
};

